<template>
  <div v-if="!isOnChatPage" class="mini-chat-wrapper">
    <!-- Floating Action Button -->
    <transition name="bounce">
      <button
        v-show="!open"
        class="mini-chat__fab"
        type="button"
        @click="toggle"
        :title="$t('chat.mini_title') || 'Messages'"
      >
        <i class="bi bi-chat-dots-fill"></i>
        <span v-if="unreadTotal > 0" class="mini-chat__badge">{{
          unreadTotal > 99 ? "99+" : unreadTotal
        }}</span>
      </button>
    </transition>

    <!-- Chat Window -->
    <transition name="slide-up">
      <div v-if="open" class="mini-chat__window">
        <!-- Header -->
        <div class="mini-chat__header">
          <div class="mini-chat__header-left">
            <button
              v-if="view === 'conversation'"
              class="mini-chat__back"
              @click="backToList"
            >
              <i class="bi bi-arrow-right"></i>
            </button>
            <div
              v-if="view === 'conversation' && current"
              class="mini-chat__header-conv-info"
            >
              <span class="mini-chat__title">{{ displayName(current) }}</span>
              <!-- Last Seen Recently / Typing indicator (Flutter sync: lastSeenRecently) -->
              <span
                v-if="isOtherUserTyping"
                class="mini-chat__subtitle mini-chat__typing-text"
              >
                <span class="mini-typing-dots"
                  ><span></span><span></span><span></span
                ></span>
                {{ $t("chat.typing") || "typing..." }}
              </span>
              <span v-else class="mini-chat__subtitle">{{
                conversationStatusText
              }}</span>
            </div>
            <span v-else class="mini-chat__title">{{
              $t("chat.mini_title") || "Messages"
            }}</span>
          </div>
          <div class="mini-chat__header-actions">
            <!-- Mia Summary Button (Flutter sync: _showChatSummary) -->
            <button
              v-if="view === 'conversation' && current"
              class="mini-chat__mia-btn"
              @click="showSummaryDialog = true"
              :title="$t('chat.summarize_chat') || 'Summarize with Mia'"
            >
              <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon" />
            </button>
            <!-- Conversation Actions Menu (Block/Unblock) -->
            <div
              v-if="view === 'conversation' && current"
              class="mini-chat__conv-menu-wrap"
            >
              <button
                class="mini-chat__menu-btn"
                @click="showConvMenu = !showConvMenu"
                :title="$t('common.more') || 'More'"
              >
                <i class="bi bi-three-dots-vertical"></i>
              </button>
              <transition name="menu-fade">
                <div v-if="showConvMenu" class="mini-chat__conv-dropdown">
                  <!-- DM actions -->
                  <template v-if="!isGroupChat">
                    <button
                      v-if="!isBlocked"
                      class="mini-chat__conv-dropdown-item mini-chat__conv-dropdown-item--danger"
                      @click="blockUser"
                    >
                      <i class="bi bi-slash-circle"></i>
                      {{ $t("chat.block_user") || "Block User" }}
                    </button>
                    <button
                      v-else-if="isUserBlocker"
                      class="mini-chat__conv-dropdown-item mini-chat__conv-dropdown-item--success"
                      @click="unblockUser"
                    >
                      <i class="bi bi-check-circle"></i>
                      {{ $t("chat.unblock_user") || "Unblock User" }}
                    </button>
                  </template>
                  <!-- Group actions -->
                  <template v-else>
                    <button
                      v-if="isCurrentUserOwner"
                      class="mini-chat__conv-dropdown-item"
                      @click="toggleChatLock"
                    >
                      <i
                        :class="isChatLocked ? 'bi bi-unlock' : 'bi bi-lock'"
                      ></i>
                      {{
                        isChatLocked
                          ? $t("chat.unlock_chat") || "Unlock Chat"
                          : $t("chat.lock_chat") || "Lock Chat"
                      }}
                    </button>
                    <button
                      class="mini-chat__conv-dropdown-item mini-chat__conv-dropdown-item--danger"
                      @click="leaveGroup"
                    >
                      <i class="bi bi-box-arrow-right"></i>
                      {{ $t("chat.leave_group") || "Leave Group" }}
                    </button>
                  </template>
                </div>
              </transition>
            </div>
            <router-link
              class="mini-chat__expand"
              :to="{ name: 'ChatInbox' }"
              @click="open = false"
              :title="$t('chat.open_full') || 'Open full'"
            >
              <i class="bi bi-arrows-fullscreen"></i>
            </router-link>
            <button
              class="mini-chat__close"
              @click="open = false"
              :title="$t('common.close') || 'Close'"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="mini-chat__content">
          <!-- Not logged in -->
          <div v-if="!isLoggedIn" class="mini-chat__empty">
            <i class="bi bi-lock"></i>
            <p>
              {{ $t("chat.please_login") || "Please login to access messages" }}
            </p>
            <router-link
              class="mini-chat__login-btn"
              :to="{ name: 'Login' }"
              @click="open = false"
            >
              {{ $t("common.login") || "Login" }}
            </router-link>
          </div>

          <!-- Inbox List -->
          <div v-else-if="view === 'list'" class="mini-chat__list">
            <!-- Search Field (Flutter sync: search conversations) -->
            <div class="mini-chat__search">
              <i class="bi bi-search mini-chat__search-icon"></i>
              <input
                v-model="searchQuery"
                type="text"
                class="mini-chat__search-input"
                :placeholder="
                  $t('chat.search_conversations') || 'Search conversations...'
                "
              />
              <button
                v-if="searchQuery"
                class="mini-chat__search-clear"
                @click="searchQuery = ''"
              >
                <i class="bi bi-x"></i>
              </button>
            </div>
            <!-- Filter Tabs (Flutter sync: ConversationFilter - reused from Master.vue) -->
            <div class="mini-chat__filter-tabs">
              <button
                class="mini-chat__filter-tab"
                :class="{ active: conversationFilter === 'all' }"
                @click="conversationFilter = 'all'"
              >
                {{ $t("chat.filter_all") || "All" }}
              </button>
              <button
                class="mini-chat__filter-tab"
                :class="{ active: conversationFilter === 'unread' }"
                @click="conversationFilter = 'unread'"
              >
                {{ $t("chat.filter_unread") || "Unread" }}
              </button>
              <button
                class="mini-chat__filter-tab"
                :class="{ active: conversationFilter === 'groups' }"
                @click="conversationFilter = 'groups'"
              >
                {{ $t("chat.filter_groups") || "Groups" }}
              </button>
              <button
                class="mini-chat__filter-tab"
                :class="{ active: conversationFilter === 'direct' }"
                @click="conversationFilter = 'direct'"
              >
                {{ $t("chat.filter_direct") || "Direct" }}
              </button>
            </div>
            <div v-if="loading" class="mini-chat__loading">
              <div class="mini-chat__spinner"></div>
              <span>{{ $t("common.loading") || "Loading..." }}</span>
            </div>
            <div v-else-if="error" class="mini-chat__error">
              <i class="bi bi-exclamation-triangle"></i>
              <span>{{ error }}</span>
              <button @click="fetchConversations">
                {{ $t("common.retry") || "Retry" }}
              </button>
            </div>
            <div
              v-else-if="filteredConversations.length === 0"
              class="mini-chat__empty-list"
            >
              <i class="bi bi-chat-square-dots"></i>
              <p>
                {{
                  conversationFilter === "all" && !searchQuery
                    ? $t("chat.no_conversations") || "No conversations yet"
                    : $t("chat.no_matching_conversations") ||
                      "No matching conversations"
                }}
              </p>
            </div>
            <div v-else class="mini-chat__conversations">
              <div
                v-for="c in filteredConversations"
                :key="c.id"
                class="mini-chat__conv-item"
                :class="{
                  'mini-chat__conv-item--unread': isConversationUnread(c),
                }"
                @click="openConversation(c)"
              >
                <div class="mini-chat__conv-avatar">
                  <img
                    :src="getConversationAvatar(c)"
                    @error="onImgError"
                    alt=""
                  />
                  <span
                    v-if="isConversationUnread(c)"
                    class="mini-chat__conv-avatar-dot"
                  ></span>
                </div>
                <div class="mini-chat__conv-info">
                  <div class="mini-chat__conv-header">
                    <span
                      class="mini-chat__conv-name"
                      :class="{
                        'mini-chat__conv-name--unread': isConversationUnread(c),
                      }"
                      >{{ displayName(c) }}</span
                    >
                    <span
                      v-if="getUnreadCount(c) > 0"
                      class="mini-chat__conv-badge"
                      >{{
                        getUnreadCount(c) > 99 ? "99+" : getUnreadCount(c)
                      }}</span
                    >
                  </div>
                  <div
                    class="mini-chat__conv-preview"
                    :class="{
                      'mini-chat__conv-preview--unread':
                        isConversationUnread(c),
                    }"
                  >
                    {{ lastMessagePreview(c) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Conversation -->
          <div v-else class="mini-chat__conversation">
            <!-- Messages Loading -->
            <div v-if="messagesLoading" class="mini-chat__messages-loading">
              <div class="mini-chat__spinner"></div>
              <span>{{
                $t("chat.loading_messages") || "Loading messages..."
              }}</span>
            </div>
            <div v-else ref="list" class="mini-chat__messages">
              <div
                v-for="m in displayMessages"
                :key="m.id"
                class="mini-msg-row"
                :class="{
                  'mini-msg-row--own': isMe(m),
                  'mini-msg-row--unread': isMessageUnread(m),
                }"
              >
                <div
                  class="mini-msg-bubble"
                  :class="{
                    'mini-msg-bubble--own': isMe(m),
                    'mini-msg-bubble--pending': isPending(m),
                    'mini-msg-bubble--failed': isFailed(m),
                    'mini-msg-bubble--media': hasAttachment(m),
                    'mini-msg-bubble--unread': isMessageUnread(m),
                  }"
                >
                  <!-- Media Content -->
                  <div v-if="hasAttachment(m)" class="mini-msg-media">
                    <!-- Image -->
                    <template v-if="getSubtype(m) === 'image'">
                      <div class="mini-img-wrap">
                        <img
                          v-if="getFileUrl(m)"
                          :src="getFileUrl(m)"
                          alt=""
                          class="mini-chat-img"
                          @click="openImage(getFileUrl(m))"
                        />
                        <div v-else class="mini-img-placeholder">
                          <i class="bi bi-image"></i>
                        </div>
                      </div>
                    </template>
                    <!-- Voice -->
                    <template v-else-if="getSubtype(m) === 'voice'">
                      <div class="mini-voice-msg">
                        <div class="mini-voice-icon">
                          <i class="bi bi-mic-fill"></i>
                        </div>
                        <div class="mini-voice-content">
                          <audio
                            v-if="getFileUrl(m)"
                            :src="getFileUrl(m)"
                            controls
                            preload="metadata"
                            class="mini-voice-audio"
                          ></audio>
                          <span v-else class="mini-voice-label">{{
                            $t("chat.msg_voice") || "Voice"
                          }}</span>
                        </div>
                      </div>
                    </template>
                    <!-- File/Document -->
                    <template v-else>
                      <div class="mini-file-msg">
                        <div class="mini-file-icon">
                          <i :class="getFileIcon(m)"></i>
                        </div>
                        <div class="mini-file-info">
                          <span class="mini-file-name">{{
                            getFilename(m) ||
                            $t("chat.msg_document") ||
                            "Document"
                          }}</span>
                          <a
                            v-if="getFileUrl(m)"
                            :href="getFileUrl(m)"
                            target="_blank"
                            rel="noopener"
                            class="mini-file-download"
                          >
                            <i class="bi bi-download"></i>
                            {{ $t("common.download") || "Download" }}
                          </a>
                        </div>
                      </div>
                    </template>
                  </div>

                  <!-- Text Content -->
                  <p v-if="m.text && m.text.length" class="mini-msg-text">
                    {{ m.text }}
                  </p>

                  <!-- Meta (time + status) -->
                  <div class="mini-msg-meta">
                    <span class="mini-msg-time">{{
                      formatTime(m.createdAt)
                    }}</span>
                    <span v-if="isPending(m)" class="mini-msg-spinner"></span>
                    <span
                      v-else-if="isFailed(m)"
                      class="mini-msg-status--failed"
                      ><i class="bi bi-exclamation-circle-fill"></i
                    ></span>
                    <span v-else-if="isMe(m)" class="mini-msg-status--sent"
                      ><i class="bi bi-check2-all"></i
                    ></span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Blocker Banner (Flutter sync: _buildBlockerBanner) -->
            <div v-if="isUserBlocker" class="mini-chat__blocker-banner">
              <i class="bi bi-info-circle"></i>
              <span>{{
                $t("chat.you_blocked_user") || "You have blocked this user."
              }}</span>
              <button
                class="mini-chat__blocker-banner-btn"
                @click="unblockUser"
              >
                {{ $t("chat.unblock") || "Unblock" }}
              </button>
            </div>

            <!-- Input Area -->
            <div v-if="canSend" class="mini-chat__input-area">
              <input
                v-model="text"
                @keyup.enter="send"
                :placeholder="$t('common.type_message') || 'Type a message...'"
                class="mini-chat__input"
                :disabled="isSending"
              />
              <button
                class="mini-chat__send"
                :disabled="isSending || !text.trim()"
                @click="send"
              >
                <span v-if="isSending" class="mini-chat__send-spinner"></span>
                <i v-else class="bi bi-send-fill"></i>
              </button>
            </div>
            <div v-else class="mini-chat__blocked">
              <i class="bi bi-lock-fill"></i>
              <span>{{ cannotSendReason }}</span>
            </div>
          </div>
        </div>

        <!-- Mia Summary Dialog (Flutter sync: _showChatSummary) -->
        <transition name="fade">
          <div
            v-if="showSummaryDialog"
            class="mini-chat__overlay"
            @click.self="showSummaryDialog = false"
          >
            <div class="mini-chat__summary-dialog">
              <div class="mini-chat__summary-header">
                <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon-small" />
                <span>{{
                  $t("chat.summarize_chat_mia") || "Summarize Chat with Mia"
                }}</span>
                <button
                  class="mini-chat__summary-close"
                  @click="showSummaryDialog = false"
                >
                  <i class="bi bi-x"></i>
                </button>
              </div>
              <div class="mini-chat__summary-body">
                <!-- Date Preset Chips -->
                <div class="mini-chat__summary-presets">
                  <button
                    class="mini-chat__summary-chip"
                    :class="{ active: summaryPreset === 'today' }"
                    @click="selectSummaryPreset('today')"
                  >
                    {{ $t("chat.today") || "Today" }}
                  </button>
                  <button
                    class="mini-chat__summary-chip"
                    :class="{ active: summaryPreset === 'yesterday' }"
                    @click="selectSummaryPreset('yesterday')"
                  >
                    {{ $t("chat.yesterday") || "Yesterday" }}
                  </button>
                  <button
                    class="mini-chat__summary-chip"
                    :class="{ active: summaryPreset === 'custom' }"
                    @click="selectSummaryPreset('custom')"
                  >
                    {{ $t("chat.custom") || "Custom" }}
                  </button>
                </div>
                <!-- Custom Date Pickers -->
                <div
                  v-if="summaryPreset === 'custom'"
                  class="mini-chat__summary-dates"
                >
                  <label class="mini-chat__summary-date-label">
                    <i class="bi bi-calendar3"></i>
                    {{ $t("chat.start_date") || "Start Date" }}
                    <input
                      type="date"
                      v-model="summaryStartDate"
                      class="mini-chat__summary-date-input"
                      :max="summaryEndDate || todayStr"
                    />
                  </label>
                  <label class="mini-chat__summary-date-label">
                    <i class="bi bi-calendar3"></i>
                    {{ $t("chat.end_date") || "End Date" }}
                    <input
                      type="date"
                      v-model="summaryEndDate"
                      class="mini-chat__summary-date-input"
                      :min="summaryStartDate"
                      :max="todayStr"
                    />
                  </label>
                </div>
                <!-- Summary Result -->
                <div v-if="summaryLoading" class="mini-chat__summary-loading">
                  <div class="mini-chat__spinner"></div>
                  <span>{{
                    $t("chat.generating_summary") || "Generating summary..."
                  }}</span>
                </div>
                <div
                  v-else-if="summaryResult"
                  class="mini-chat__summary-result"
                >
                  <div class="mini-chat__summary-result-header">
                    <span>{{ $t("chat.chat_summary") || "Chat Summary" }}</span>
                    <button
                      class="mini-chat__summary-copy"
                      @click="copySummary"
                      :title="$t('chat.copy_summary') || 'Copy'"
                    >
                      <i class="bi bi-clipboard"></i>
                    </button>
                  </div>
                  <div class="mini-chat__summary-text">{{ summaryResult }}</div>
                </div>
                <div v-else-if="summaryError" class="mini-chat__summary-error">
                  <i class="bi bi-exclamation-triangle"></i>
                  <span>{{ summaryError }}</span>
                </div>
              </div>
              <div class="mini-chat__summary-footer">
                <button
                  class="mini-chat__summary-cancel"
                  @click="showSummaryDialog = false"
                >
                  {{ $t("common.cancel") || "Cancel" }}
                </button>
                <button
                  class="mini-chat__summary-submit"
                  :disabled="summaryLoading"
                  @click="generateSummary"
                >
                  <i class="fas fa-magic"></i>
                  {{ $t("chat.summarize") || "Summarize" }}
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { ChatService } from "@/services/chat";
import { chatState } from "@/stores/chat";
import Swal from "sweetalert2";
import { getSwalConfig } from "@/utils/swal-config";

export default {
  name: "MiniChat",
  data() {
    return {
      open: false,
      view: "list",
      conversations: [],
      loading: false,
      messagesLoading: false,
      error: null,
      current: null,
      messagesRaw: [],
      lastMessageId: null,
      poller: null,
      isSending: false,
      text: "",
      canSend: true,
      isChatLocked: false,
      isUserBlocked: false,
      cannotSendReason: "",
      // Search
      searchQuery: "",
      // Filters (Flutter sync: ConversationFilter - reused from Master.vue)
      conversationFilter: "all",
      // Block/Unblock (Flutter sync)
      isBlocked: false,
      isUserBlocker: false,
      isCurrentUserOwner: false,
      isGroupChat: false,
      // Conversation actions menu
      showConvMenu: false,
      // Typing indicator (Flutter sync: _isOtherUserTyping)
      isOtherUserTyping: false,
      typingUserName: null,
      // Mia Summary dialog
      showSummaryDialog: false,
      summaryPreset: "today",
      summaryStartDate: "",
      summaryEndDate: "",
      summaryLoading: false,
      summaryResult: null,
      summaryError: null,
    };
  },
  computed: {
    isLoggedIn() {
      try {
        return !!localStorage.getItem("access_token");
      } catch (_) {
        return false;
      }
    },
    // Hide MiniChat when on expanded chat pages
    isOnChatPage() {
      const routeName = this.$route?.name || "";
      return ["ChatInbox", "ChatConversation"].includes(routeName);
    },
    unreadTotal() {
      return this.conversations.reduce(
        (acc, c) => acc + (c.unreadMessageCount || c.unread_count || 0),
        0
      );
    },
    displayMessages() {
      return [...this.messagesRaw].reverse();
    },
    // Flutter sync: ChatInboxProvider._applyFilter logic (reused from Master.vue)
    filteredConversations() {
      let filtered = this.conversations;

      // Apply filter
      switch (this.conversationFilter) {
        case "unread":
          filtered = filtered.filter((c) => this.isConversationUnread(c));
          break;
        case "groups":
          filtered = filtered.filter((c) => {
            const type = c.custom?.type || "";
            return (
              type === "group" ||
              type === "student_group" ||
              type === "course_group"
            );
          });
          break;
        case "direct":
          filtered = filtered.filter((c) => {
            const type = c.custom?.type || "";
            return type === "direct";
          });
          break;
        case "all":
        default:
          break;
      }

      // Apply search
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        filtered = filtered.filter((c) => {
          const name = this.displayName(c).toLowerCase();
          const preview = this.lastMessagePreview(c).toLowerCase();
          return name.includes(q) || preview.includes(q);
        });
      }

      return filtered;
    },
    // Flutter sync: lastSeenRecently / statusText
    conversationStatusText() {
      if (this.isUserBlocked)
        return this.$t("chat.blocked_message") || "You are blocked";
      if (this.isChatLocked && this.canSend)
        return (
          this.$t("chat.locked_owner_hint") ||
          "Chat is locked (owners can still send)"
        );
      if (this.isChatLocked) return this.$t("chat.locked") || "Chat is locked";
      return this.$t("chat.last_seen_recently") || "last seen recently";
    },
    todayStr() {
      return new Date().toISOString().split("T")[0];
    },
  },
  watch: {
    showSummaryDialog(val) {
      if (val) {
        this.summaryPreset = "today";
        this.summaryStartDate = this.todayStr;
        this.summaryEndDate = this.todayStr;
        this.summaryResult = null;
        this.summaryError = null;
      }
    },
    showConvMenu(val) {
      if (val) {
        setTimeout(() => {
          document.addEventListener("click", this._closeConvMenuOutside);
        }, 10);
      } else {
        document.removeEventListener("click", this._closeConvMenuOutside);
      }
    },
  },
  mounted() {
    this._closeConvMenuOutside = (e) => {
      const wrap = this.$el?.querySelector(".mini-chat__conv-menu-wrap");
      if (wrap && !wrap.contains(e.target)) {
        this.showConvMenu = false;
      }
    };
    // Preload inbox lazily
    if (this.isLoggedIn) this.fetchConversations();
    // Theme detection for chat background (only if element exists)
    if (this.$el) {
      this.updateChatBackground();
      this.themeObserver = new MutationObserver(() =>
        this.updateChatBackground()
      );
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-bs-theme", "data-theme"],
      });
    }
  },
  beforeUnmount() {
    if (this.poller) clearInterval(this.poller);
    if (this.themeObserver) this.themeObserver.disconnect();
    document.removeEventListener("click", this._closeConvMenuOutside);
  },
  methods: {
    updateChatBackground() {
      // Guard: skip if element doesn't exist (component hidden by v-if)
      if (!this.$el || typeof this.$el.querySelector !== "function") return;

      // Detect theme from Metronic's data-bs-theme attribute
      const theme =
        document.documentElement.getAttribute("data-bs-theme") ||
        document.documentElement.getAttribute("data-theme") ||
        "light";
      const isDark = theme === "dark";

      // Toggle dark mode class on mini chat window (CSS handles the backgrounds)
      const miniWindow = this.$el.querySelector(".mini-chat__window");
      if (miniWindow) {
        if (isDark) {
          miniWindow.classList.add("mini-chat-dark");
        } else {
          miniWindow.classList.remove("mini-chat-dark");
        }
      }
    },
    toggle() {
      this.open = !this.open;
      if (
        this.open &&
        this.view === "list" &&
        this.isLoggedIn &&
        !this.conversations.length
      ) {
        this.fetchConversations();
      }
      // Update background when opening
      if (this.open) this.$nextTick(() => this.updateChatBackground());
    },
    async fetchConversations() {
      this.loading = true;
      this.error = null;
      try {
        const list = await ChatService.getConversations();
        this.conversations = Array.isArray(list) ? list.slice(0, 50) : [];
      } catch (e) {
        this.error = this.$t("chat.failed_to_load") || "Failed to load chat";
      } finally {
        this.loading = false;
      }
    },
    // Get the other participant's info for direct chats
    getDirectChatPeer(c) {
      const custom = c.custom || {};
      if (custom.type !== "direct") return null;

      const currentUserId = String(chatState.userId || "");
      let details = [];
      try {
        const raw = custom.participants_details;
        if (typeof raw === "string" && raw.trim().length) {
          details = JSON.parse(raw);
        } else if (Array.isArray(raw)) {
          details = raw;
        }
      } catch (_) {
        return null;
      }

      if (!Array.isArray(details) || !details.length) return null;
      const other = details.find((p) => p && String(p.id) !== currentUserId);
      return other || details[0] || null;
    },
    displayName(c) {
      // For direct chats, show the other participant's name
      const peer = this.getDirectChatPeer(c);
      if (peer && peer.name) return peer.name;

      if (c.subject) return c.subject;
      if (c.participantNames) {
        const names = Object.values(c.participantNames);
        if (names && names.length) return names[0];
      }
      return this.$t("common.chat") || "Chat";
    },
    // Get avatar URL for conversation (direct chat peer or conversation photo)
    getConversationAvatar(c) {
      const peer = this.getDirectChatPeer(c);
      if (peer && peer.image) return peer.image;
      return c.photoUrl || c.photo_url || "/assets/img/acad-icon.png";
    },
    lastMessagePreview(c) {
      const lm = c.lastMessage || c.last_message;
      if (!lm) return "";
      if (lm.text && lm.text.length > 0) return lm.text;
      const content = lm.content;
      if (
        Array.isArray(content) &&
        content.length &&
        content[0].type === "file"
      ) {
        const subtype = content[0].subtype;
        if (subtype === "image") return this.$t("chat.msg_photo") || "Photo";
        if (subtype === "video") return this.$t("chat.msg_video") || "Video";
        if (subtype === "voice") return this.$t("chat.msg_voice") || "Voice";
        return this.$t("chat.msg_document") || "Attachment";
      }
      return this.$t("common.message") || "Message";
    },
    onImgError(e) {
      e.target.src = "/assets/img/acad-icon.png";
    },
    // Check if conversation has unread messages
    isConversationUnread(c) {
      return (
        c.isUnread === true ||
        c.is_unread === true ||
        this.getUnreadCount(c) > 0
      );
    },
    // Get unread message count for a conversation
    getUnreadCount(c) {
      return c.unreadMessageCount || c.unread_count || 0;
    },
    // Optimistically mark conversation as read in local state
    markConversationAsReadLocally(conversationId) {
      const idx = this.conversations.findIndex(
        (c) => (c.talkjs_conversation_id || c.id) === conversationId
      );
      if (idx !== -1) {
        // Create updated conversation with unread cleared
        const updated = {
          ...this.conversations[idx],
          unreadMessageCount: 0,
          unread_count: 0,
          isUnread: false,
          is_unread: false,
        };
        this.conversations.splice(idx, 1, updated);
      }
    },
    async openConversation(c) {
      this.current = c;
      this.view = "conversation";
      this.messagesLoading = true;
      this.updateChatBackground();

      // Optimistically mark as read immediately
      const conversationId = c.talkjs_conversation_id || c.id;
      this.markConversationAsReadLocally(conversationId);

      try {
        await this.loadConversationMeta();
        await this.loadMessages();
        this.startPolling();
      } finally {
        this.messagesLoading = false;
        this.$nextTick(() => {
          this.scrollToBottom();
          this.updateChatBackground();
        });
      }
    },
    backToList() {
      this.view = "list";
      this.current = null;
      this.messagesRaw = [];
      this.showConvMenu = false;
      this.isBlocked = false;
      this.isUserBlocker = false;
      this.isUserBlocked = false;
      this.isGroupChat = false;
      this.isCurrentUserOwner = false;
      this.isOtherUserTyping = false;
      this.typingUserName = null;
      if (this.poller) clearInterval(this.poller);
    },
    loadConversationMeta() {
      // Use this.current directly (from conversations list) - same data source as filters
      const c = this.current;
      if (!c) {
        this.isGroupChat = false;
        this.isBlocked = false;
        this.isUserBlocker = false;
        this.isUserBlocked = false;
        this.isCurrentUserOwner = false;
        this.isChatLocked = false;
        this.canSend = true;
        this.cannotSendReason = "";
        return;
      }

      const custom = c.custom || {};
      const currentUserId = String(chatState.userId || "");

      // Detect conversation type (same logic as filteredConversations)
      const type = custom.type || "";
      this.isGroupChat =
        type === "group" || type === "student_group" || type === "course_group";

      // Chat enabled
      let isEnabled = true;
      if (custom.chat_enabled === false) isEnabled = false;
      if (typeof custom.chat_enabled === "string")
        isEnabled = custom.chat_enabled.toLowerCase() === "true";
      this.isChatLocked = !isEnabled;

      // Block status (Flutter sync: isBlocked, isUserBlocker, isUserBlocked)
      const blockedBy = custom.blocked_by;
      this.isBlocked = !!blockedBy;
      this.isUserBlocker = !!blockedBy && String(blockedBy) === currentUserId;
      this.isUserBlocked = !!blockedBy && String(blockedBy) !== currentUserId;

      // Owner check (Flutter sync: _isCurrentUserOwner)
      const creatorId =
        custom.creator_id != null ? String(custom.creator_id) : null;
      this.isCurrentUserOwner = !!(creatorId && creatorId === currentUserId);

      // Can send logic (Flutter sync)
      this.canSend =
        !this.isUserBlocked && (isEnabled || this.isCurrentUserOwner);
      if (this.isUserBlocked) {
        this.cannotSendReason =
          this.$t("chat.blocked_message") ||
          "You cannot send messages to this user. They have blocked you.";
      } else if (this.isChatLocked && !this.isCurrentUserOwner) {
        this.cannotSendReason = this.$t("chat.locked") || "Chat is locked";
      } else {
        this.cannotSendReason = "";
      }
    },
    async loadMessages() {
      const id = this.current.talkjs_conversation_id || this.current.id;
      const list = await ChatService.getMessages({ conversationId: id });
      this.messagesRaw = Array.isArray(list) ? list : [];
      this.lastMessageId = this.messagesRaw.length
        ? this.messagesRaw[0].id
        : null;
      if (this.messagesRaw.length)
        await ChatService.markAsRead({ conversationId: id });
    },
    startPolling() {
      if (this.poller) clearInterval(this.poller);
      const id = this.current.talkjs_conversation_id || this.current.id;
      this.poller = setInterval(async () => {
        if (!this.lastMessageId) return;
        try {
          const newOnes = await ChatService.getMessages({
            conversationId: id,
            limit: 50,
            afterMessageId: this.lastMessageId,
          });
          if (Array.isArray(newOnes) && newOnes.length) {
            // Get existing real message IDs (exclude temp/optimistic)
            const existingRealIds = new Set(
              this.messagesRaw
                .filter(
                  (m) => !String(m.id).startsWith("temp_") && !m._isOptimistic
                )
                .map((m) => String(m.id))
            );
            const uniques = newOnes.filter(
              (m) => !existingRealIds.has(String(m.id))
            );
            if (uniques.length) {
              // Preserve localPath from matching optimistic messages
              const enhancedUniques = uniques.map((u) => {
                const matchingOptimistic = this.messagesRaw.find(
                  (m) =>
                    (String(m.id).startsWith("temp_") || m._isOptimistic) &&
                    String(u.senderId) === String(m.senderId) &&
                    m.custom &&
                    m.custom.localPath
                );
                if (
                  matchingOptimistic &&
                  matchingOptimistic.custom &&
                  matchingOptimistic.custom.localPath
                ) {
                  return {
                    ...u,
                    custom: {
                      ...(u.custom || {}),
                      localPath: matchingOptimistic.custom.localPath,
                    },
                  };
                }
                return u;
              });

              // Remove optimistic messages that match new real messages
              const newMessages = this.messagesRaw.filter((m) => {
                if (!String(m.id).startsWith("temp_") && !m._isOptimistic)
                  return true;
                const matchingReal = enhancedUniques.find(
                  (u) =>
                    u.text === m.text &&
                    String(u.senderId) === String(m.senderId) &&
                    Math.abs((u.createdAt || 0) - (m.createdAt || 0)) < 60000
                );
                return !matchingReal;
              });
              newMessages.unshift(...enhancedUniques);
              this.messagesRaw = newMessages;
              this.lastMessageId = uniques[0].id;
              await ChatService.markAsRead({ conversationId: id });
              this.$nextTick(this.scrollToBottomSmooth);
            }
          }
        } catch (_) {}
      }, 3000);
    },
    isMe(m) {
      return (
        m.senderId && String(m.senderId) === String(chatState.userId || "")
      );
    },
    isPending(m) {
      return !!(m.custom && m.custom.isPending);
    },
    isFailed(m) {
      return !!(m.custom && m.custom.isFailed);
    },
    // Check if a message is unread (not sent by current user and not in readBy)
    isMessageUnread(m) {
      if (this.isMe(m)) return false;
      if (this.isPending(m) || this.isFailed(m)) return false;
      const currentUserId = String(chatState.userId || "");
      const readBy = m.readBy || [];
      return (
        !readBy.includes(currentUserId) &&
        !readBy.includes(parseInt(currentUserId))
      );
    },
    hasAttachment(m) {
      const content = m.content;
      if (
        Array.isArray(content) &&
        content.length &&
        content[0].type === "file"
      )
        return true;
      if (m.attachment) return true;
      return false;
    },
    getFileData(m) {
      const content = m.content;
      if (
        Array.isArray(content) &&
        content.length &&
        content[0].type === "file"
      )
        return content[0];
      if (m.attachment) return m.attachment;
      return null;
    },
    getSubtype(m) {
      const fd = this.getFileData(m);
      return (fd && fd.subtype) || "document";
    },
    getFileUrl(m) {
      const fd = this.getFileData(m);
      return (fd && fd.url) || null;
    },
    getFilename(m) {
      const fd = this.getFileData(m);
      return (fd && (fd.filename || fd.name || fd.file_name)) || null;
    },
    getFileIcon(m) {
      const name = (this.getFilename(m) || "").toLowerCase();
      if (/\.pdf$/.test(name)) return "bi bi-file-earmark-pdf-fill";
      if (/\.(doc|docx)$/.test(name)) return "bi bi-file-earmark-word-fill";
      if (/\.(xls|xlsx)$/.test(name)) return "bi bi-file-earmark-excel-fill";
      if (/\.(ppt|pptx)$/.test(name)) return "bi bi-file-earmark-ppt-fill";
      if (/\.(zip|rar|7z)$/.test(name)) return "bi bi-file-earmark-zip-fill";
      return "bi bi-file-earmark-fill";
    },
    openImage(url) {
      if (url) window.open(url, "_blank");
    },
    attachmentLabel(m) {
      const content = m.content;
      if (
        Array.isArray(content) &&
        content.length &&
        content[0].type === "file"
      ) {
        const subtype = content[0].subtype;
        if (subtype === "image") return this.$t("chat.msg_photo") || "Photo";
        if (subtype === "video") return this.$t("chat.msg_video") || "Video";
        if (subtype === "voice") return this.$t("chat.msg_voice") || "Voice";
        return this.$t("chat.msg_document") || "Attachment";
      }
      return this.$t("common.attachment") || "Attachment";
    },
    formatTime(ts) {
      try {
        const d = new Date(typeof ts === "number" ? ts : Date.parse(ts));
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      } catch (_) {
        return "";
      }
    },
    async send() {
      const text = (this.text || "").trim();
      if (!text || this.isSending || !this.canSend) return;

      this.isSending = true;
      this.text = "";
      const conversationId =
        this.current.talkjs_conversation_id || this.current.id;

      // Callback when message is confirmed in GET response
      const onConfirmed = (realMessage, tempId) => {
        const idx = this.messagesRaw.findIndex((m) => m.id === tempId);
        const optimisticMsg = idx !== -1 ? this.messagesRaw[idx] : null;

        if (realMessage) {
          // Preserve localPath from optimistic message for smooth image transition
          let enhancedMessage = realMessage;
          if (
            optimisticMsg &&
            optimisticMsg.custom &&
            optimisticMsg.custom.localPath
          ) {
            enhancedMessage = {
              ...realMessage,
              custom: {
                ...(realMessage.custom || {}),
                localPath: optimisticMsg.custom.localPath,
              },
            };
          }

          if (idx !== -1) {
            this.messagesRaw.splice(idx, 1, enhancedMessage);
          } else {
            const exists = this.messagesRaw.some(
              (m) => String(m.id) === String(realMessage.id)
            );
            if (!exists) this.messagesRaw.unshift(enhancedMessage);
          }
          this.lastMessageId = realMessage.id;
        } else {
          // Message not found after polling - just clear pending state
          if (idx !== -1) {
            const msg = this.messagesRaw[idx];
            this.messagesRaw.splice(idx, 1, {
              ...msg,
              custom: { ...(msg.custom || {}), isPending: false },
            });
          }
        }
        this.isSending = false;
      };

      try {
        const sent = await ChatService.sendMessage({
          conversationId,
          text,
          onConfirmed,
        });
        if (sent && sent.id) {
          this.messagesRaw.unshift(sent);
          this.$nextTick(this.scrollToBottomSmooth);
        }
      } catch (err) {
        console.warn("[MiniChat] send error:", err);
        this.isSending = false;
      }
    },
    scrollToBottom() {
      const el = this.$refs.list;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    },
    scrollToBottomSmooth() {
      const el = this.$refs.list;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    },
    // ==================== Block/Unblock (Flutter Sync) ====================
    async blockUser() {
      this.showConvMenu = false;
      if (this.isGroupChat) return;
      const result = await Swal.fire({
        ...getSwalConfig("warning"),
        title: this.$t("chat.block_user") || "Block User",
        text:
          this.$t("chat.block_user_confirmation") ||
          "Are you sure you want to block this user?",
        showCancelButton: true,
        confirmButtonText: this.$t("common.confirm") || "Confirm",
        cancelButtonText: this.$t("common.cancel") || "Cancel",
        confirmButtonColor: "#ef4444",
      });
      if (!result.isConfirmed) return;
      try {
        const conversationId =
          this.current.talkjs_conversation_id || this.current.id;
        const res = await ChatService.blockUser(conversationId);
        if (res.success) {
          this.isBlocked = true;
          this.isUserBlocker = true;
          this.isUserBlocked = false;
          this.canSend = true;
          Swal.fire({
            ...getSwalConfig("success"),
            title: this.$t("chat.user_blocked") || "User has been blocked",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            ...getSwalConfig("error"),
            title:
              res.message ||
              this.$t("chat.failed_to_block") ||
              "Failed to block user",
          });
        }
      } catch (e) {
        console.error("[MiniChat] Block user error:", e);
        Swal.fire({
          ...getSwalConfig("error"),
          title: this.$t("chat.failed_to_block") || "Failed to block user",
        });
      }
    },
    async unblockUser() {
      this.showConvMenu = false;
      if (this.isGroupChat) return;
      if (!this.isUserBlocker) return;
      const result = await Swal.fire({
        ...getSwalConfig("question"),
        title: this.$t("chat.unblock_user") || "Unblock User",
        text:
          this.$t("chat.unblock_user_confirmation") ||
          "Are you sure you want to unblock this user?",
        showCancelButton: true,
        confirmButtonText: this.$t("common.confirm") || "Confirm",
        cancelButtonText: this.$t("common.cancel") || "Cancel",
        confirmButtonColor: "#00c444",
      });
      if (!result.isConfirmed) return;
      try {
        const conversationId =
          this.current.talkjs_conversation_id || this.current.id;
        const res = await ChatService.unblockUser(conversationId);
        if (res.success) {
          this.isBlocked = false;
          this.isUserBlocker = false;
          this.isUserBlocked = false;
          this.canSend = true;
          Swal.fire({
            ...getSwalConfig("success"),
            title: this.$t("chat.user_unblocked") || "User has been unblocked",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            ...getSwalConfig("error"),
            title:
              res.message ||
              this.$t("chat.failed_to_unblock") ||
              "Failed to unblock user",
          });
        }
      } catch (e) {
        console.error("[MiniChat] Unblock user error:", e);
        Swal.fire({
          ...getSwalConfig("error"),
          title: this.$t("chat.failed_to_unblock") || "Failed to unblock user",
        });
      }
    },
    // ==================== Group Moderation (Flutter Sync) ====================
    async toggleChatLock() {
      this.showConvMenu = false;
      if (!this.isCurrentUserOwner) return;
      try {
        const conversationId =
          this.current.talkjs_conversation_id || this.current.id;
        const result = await ChatService.toggleChatLock(conversationId);
        if (result.success) {
          this.isChatLocked = !this.isChatLocked;
          this.canSend =
            !this.isUserBlocked &&
            (!this.isChatLocked || this.isCurrentUserOwner);
        } else {
          alert(result.message || "Failed to toggle chat lock");
        }
      } catch (e) {
        console.error("[MiniChat] Toggle chat lock error:", e);
      }
    },
    async leaveGroup() {
      this.showConvMenu = false;
      const result = await Swal.fire({
        ...getSwalConfig("warning"),
        title: this.$t("chat.leave_group") || "Leave Group",
        text:
          this.$t("chat.leave_group_confirmation") ||
          "Are you sure you want to leave this group?",
        showCancelButton: true,
        confirmButtonText: this.$t("common.confirm") || "Confirm",
        cancelButtonText: this.$t("common.cancel") || "Cancel",
        confirmButtonColor: "#ef4444",
      });
      if (!result.isConfirmed) return;
      try {
        const conversationId =
          this.current.talkjs_conversation_id || this.current.id;
        await ChatService.leaveConversation(conversationId);
        Swal.fire({
          ...getSwalConfig("success"),
          title:
            this.$t("chat.left_group_successfully") ||
            "You have left the group",
          timer: 2000,
          showConfirmButton: false,
        });
        this.backToList();
        this.fetchConversations();
      } catch (e) {
        console.error("[MiniChat] Leave group error:", e);
        Swal.fire({
          ...getSwalConfig("error"),
          title: this.$t("chat.failed_to_leave") || "Failed to leave group",
        });
      }
    },
    // ==================== Mia Summary (Flutter Sync: _showChatSummary) ====================
    selectSummaryPreset(preset) {
      this.summaryPreset = preset;
      this.summaryResult = null;
      this.summaryError = null;
      const now = new Date();
      if (preset === "today") {
        this.summaryStartDate = this.todayStr;
        this.summaryEndDate = this.todayStr;
      } else if (preset === "yesterday") {
        const y = new Date(now);
        y.setDate(y.getDate() - 1);
        const yStr = y.toISOString().split("T")[0];
        this.summaryStartDate = yStr;
        this.summaryEndDate = yStr;
      }
    },
    async generateSummary() {
      if (this.summaryLoading || !this.current) return;
      this.summaryLoading = true;
      this.summaryResult = null;
      this.summaryError = null;
      try {
        const conversationId =
          this.current.talkjs_conversation_id || this.current.id;
        const startDate = new Date(this.summaryStartDate || this.todayStr);
        let endDate;
        if (this.summaryPreset === "yesterday") {
          endDate = new Date(this.summaryEndDate || this.todayStr);
          endDate.setHours(23, 59, 59, 999);
        } else if (this.summaryPreset === "today") {
          endDate = new Date();
        } else {
          endDate = new Date(this.summaryEndDate || this.todayStr);
          endDate.setHours(23, 59, 59, 999);
        }
        const result = await ChatService.getChatSummary(
          conversationId,
          startDate,
          endDate
        );
        if (result.success && result.summary) {
          this.summaryResult = result.summary;
        } else {
          this.summaryError =
            result.message ||
            this.$t("chat.failed_to_load_summary") ||
            "Failed to generate summary";
        }
      } catch (e) {
        console.error("[MiniChat] Summary error:", e);
        this.summaryError =
          this.$t("chat.failed_to_load_summary") ||
          "Failed to generate summary";
      } finally {
        this.summaryLoading = false;
      }
    },
    copySummary() {
      if (this.summaryResult) {
        navigator.clipboard
          .writeText(this.summaryResult)
          .then(() => {
            alert(
              this.$t("chat.summary_copied") || "Summary copied to clipboard"
            );
          })
          .catch(() => {});
      }
    },
    noop() {},
  },
};
</script>

<style scoped>
/* ========================================
   Mini Chat - Premium Modern Design
   ======================================== */

/* CSS Variables */
:root {
  --mini-primary: #00c444;
  --mini-primary-dark: #00a838;
  --mini-bg: #e8fff1;
  --mini-text: #1f2937;
  --mini-text-muted: #6b7280;
  --mini-border: #e5e7eb;
}

/* Wrapper */
.mini-chat-wrapper {
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 1090;
}

/* ===== FLOATING ACTION BUTTON - Modern Beautiful Style ===== */
.mini-chat__fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 64px;
  height: 64px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, #00d449 0%, #00a838 50%, #008f30 100%);
  color: white !important;
  font-size: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.5), 0 3px 10px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 9998;
}

.mini-chat__fab i {
  color: white !important;
  font-size: 32px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.mini-chat__fab:hover {
  transform: translateY(-4px) scale(1.08);
  box-shadow: 0 10px 30px rgba(0, 196, 68, 0.55), 0 6px 16px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.mini-chat__fab:active {
  transform: translateY(-2px) scale(1.04);
  box-shadow: 0 5px 15px rgba(0, 196, 68, 0.45), 0 3px 8px rgba(0, 0, 0, 0.18);
}

/* RTL Support - FAB on left for Arabic */
[dir="rtl"] .mini-chat__fab,
html[lang="ar"] .mini-chat__fab {
  right: auto;
  left: 24px;
}

.mini-chat__badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: #ef4444;
  color: white;
  font-size: 12px;
  font-weight: 700;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
}

/* RTL Support - Badge position */
[dir="rtl"] .mini-chat__badge,
html[lang="ar"] .mini-chat__badge {
  right: auto;
  left: -6px;
}

@keyframes pulse-badge {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* ===== CHAT WINDOW ===== */
.mini-chat__window {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 380px;
  height: 560px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.18), 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
  z-index: 9999;
}

/* RTL Support - Window position */
[dir="rtl"] .mini-chat__window,
html[lang="ar"] .mini-chat__window {
  right: auto;
  left: 24px;
}

/* ===== HEADER ===== */
.mini-chat__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  border-bottom: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.mini-chat__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mini-chat__back {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: white !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.mini-chat__back i {
  color: white !important;
}

.mini-chat__back:hover {
  background: rgba(255, 255, 255, 0.3);
  color: white !important;
}

.mini-chat__title {
  font-size: 15px;
  font-weight: 600;
  color: white;
}

.mini-chat__header-actions {
  display: flex;
  gap: 6px;
}

.mini-chat__expand,
.mini-chat__close {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white !important;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  text-decoration: none;
}

.mini-chat__expand i,
.mini-chat__close i {
  color: white !important;
}

.mini-chat__expand:hover,
.mini-chat__close:hover {
  background: rgba(255, 255, 255, 0.3);
  color: white !important;
}

/* ===== CONTENT ===== */
.mini-chat__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Empty/Login State */
.mini-chat__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  text-align: center;
}

.mini-chat__empty i {
  font-size: 48px;
  color: #00c444;
  margin-bottom: 16px;
}

.mini-chat__empty p {
  color: var(--mini-text-muted);
  margin: 0 0 20px;
}

.mini-chat__login-btn {
  padding: 10px 24px;
  background: var(--mini-primary);
  color: white;
  border-radius: 20px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

.mini-chat__login-btn:hover {
  background: var(--mini-primary-dark);
  color: white;
}

/* Loading */
.mini-chat__loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--mini-text-muted);
}

.mini-chat__spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--mini-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error */
.mini-chat__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  color: #ef4444;
}

.mini-chat__error i {
  font-size: 32px;
}

.mini-chat__error button {
  padding: 8px 16px;
  background: #fef2f2;
  color: #ef4444;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 8px;
}

/* Empty List */
.mini-chat__empty-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--mini-text-muted);
}

.mini-chat__empty-list i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

/* ===== CONVERSATIONS LIST ===== */
.mini-chat__list {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
}

.mini-chat__conversations {
  flex: 1;
  overflow-y: auto;
  background: #ffffff;
}

.mini-chat__conv-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.mini-chat__conv-item:hover {
  background: #f9fafb;
}

/* Unread conversation item styling */
.mini-chat__conv-item--unread {
  background: #f0fdf4;
  border-left: 3px solid var(--mini-primary);
}

.mini-chat__conv-item--unread:hover {
  background: #dcfce7;
}

.mini-chat__conv-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: visible;
  flex-shrink: 0;
  position: relative;
}

.mini-chat__conv-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

/* Unread indicator dot on avatar */
.mini-chat__conv-avatar-dot {
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background: var(--mini-primary);
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 196, 68, 0.3);
}

.mini-chat__conv-info {
  flex: 1;
  min-width: 0;
}

.mini-chat__conv-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.mini-chat__conv-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--mini-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Unread conversation name - bolder */
.mini-chat__conv-name--unread {
  font-weight: 800;
  color: #111827;
}

.mini-chat__conv-badge {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--mini-primary);
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mini-chat__conv-preview {
  font-size: 13px;
  color: var(--mini-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

/* Unread conversation preview - bolder and darker */
.mini-chat__conv-preview--unread {
  font-weight: 700;
  color: #374151;
}

/* ===== CONVERSATION VIEW ===== */
.mini-chat__conversation {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.mini-chat__messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  /* Light mode - green pattern (instant load) */
  background-color: #e8f5e9;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cg fill='%2386c67c' fill-opacity='0.2'%3E%3Cpath d='M10 10L14 6l4 4-4 4z'/%3E%3Ccircle cx='50' cy='15' r='3'/%3E%3Cpath d='M70 8h6v4h-6z'/%3E%3Cpath d='M15 35l3-5h6l3 5-3 5h-6z'/%3E%3Crect x='45' y='32' width='8' height='10' rx='1'/%3E%3Ccircle cx='72' cy='38' r='4'/%3E%3Cpath d='M8 58l6 3-6 3z'/%3E%3Cpath d='M35 55l5 8h-10z'/%3E%3Crect x='60' y='55' width='12' height='6' rx='2'/%3E%3Ccircle cx='20' cy='72' r='3'/%3E%3Cpath d='M48 70l4-4 4 4-4 4z'/%3E%3Cpath d='M68 68h8v8h-8z' fill-opacity='0.15'/%3E%3C/g%3E%3C/svg%3E");
  background-size: 80px 80px;
  /* GPU acceleration */
  -webkit-overflow-scrolling: touch;
}

/* Messages Loading State */
.mini-chat__messages-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  /* Same background as messages */
  background-color: #e8f5e9;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cg fill='%2386c67c' fill-opacity='0.2'%3E%3Cpath d='M10 10L14 6l4 4-4 4z'/%3E%3Ccircle cx='50' cy='15' r='3'/%3E%3Cpath d='M70 8h6v4h-6z'/%3E%3Cpath d='M15 35l3-5h6l3 5-3 5h-6z'/%3E%3Crect x='45' y='32' width='8' height='10' rx='1'/%3E%3Ccircle cx='72' cy='38' r='4'/%3E%3Cpath d='M8 58l6 3-6 3z'/%3E%3Cpath d='M35 55l5 8h-10z'/%3E%3Crect x='60' y='55' width='12' height='6' rx='2'/%3E%3Ccircle cx='20' cy='72' r='3'/%3E%3Cpath d='M48 70l4-4 4 4-4 4z'/%3E%3Cpath d='M68 68h8v8h-8z' fill-opacity='0.15'/%3E%3C/g%3E%3C/svg%3E");
  background-size: 80px 80px;
  color: #6b7280;
  font-size: 13px;
}

/* Message Row - WhatsApp Style */
.mini-msg-row {
  display: flex;
  justify-content: flex-start;
  padding: 2px 0;
}

.mini-msg-row--own {
  justify-content: flex-end;
}

/* Unread message row - subtle highlight */
.mini-msg-row--unread {
  position: relative;
}

.mini-msg-row--unread::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 196, 68, 0.08);
  pointer-events: none;
  border-radius: 8px;
}

/* Message Bubble - Matching Full Chat */
.mini-msg-bubble {
  max-width: 80%;
  min-width: 60px;
  padding: 8px 12px;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  position: relative;
  transition: box-shadow 0.15s ease;
}

.mini-msg-bubble:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08);
}

.mini-msg-bubble--own {
  background: linear-gradient(135deg, #d9fdd3 0%, #c8f7c5 100%);
  border-bottom-right-radius: 4px;
}

.mini-msg-bubble:not(.mini-msg-bubble--own) {
  border-bottom-left-radius: 4px;
}

.mini-msg-bubble--pending {
  opacity: 0.7;
}

.mini-msg-bubble--failed {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
}

.mini-msg-bubble--media {
  padding: 4px;
  overflow: hidden;
}

.mini-msg-bubble--media .mini-msg-text {
  padding: 6px 8px 2px;
}

/* Message Text */
.mini-msg-text {
  font-size: 14px;
  line-height: 1.4;
  color: #1f2937;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}

/* Message Meta */
.mini-msg-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  margin-top: 3px;
}

.mini-msg-time {
  font-size: 10px;
  color: #9ca3af;
}

.mini-msg-bubble--own .mini-msg-time {
  color: #6b8e6b;
}

.mini-msg-spinner {
  width: 10px;
  height: 10px;
  border: 2px solid #9ca3af;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.mini-msg-status--sent {
  font-size: 12px;
  color: #34d399;
}

.mini-msg-status--failed {
  font-size: 12px;
  color: #ef4444;
}

/* Media Container */
.mini-msg-media {
  border-radius: 10px;
  overflow: hidden;
}

/* Image */
.mini-img-wrap {
  border-radius: 10px;
  overflow: hidden;
}

.mini-chat-img {
  display: block;
  width: 160px;
  height: 160px;
  object-fit: cover;
  border-radius: 10px;
  cursor: pointer;
  background: #f3f4f6;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.mini-chat-img:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.mini-img-placeholder {
  width: 100px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 10px;
  color: #9ca3af;
  font-size: 22px;
}

/* Voice Message */
.mini-voice-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 10px;
  min-width: 160px;
}

.mini-msg-bubble--own .mini-voice-msg {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.mini-voice-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #25d366;
  border-radius: 50%;
  color: white;
  font-size: 14px;
  flex-shrink: 0;
}

.mini-voice-content {
  flex: 1;
  min-width: 0;
}

.mini-voice-audio {
  width: 100%;
  height: 28px;
}

.mini-voice-label {
  font-size: 12px;
  color: #6b7280;
}

/* File/Document Message */
.mini-file-msg {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 10px;
  min-width: 160px;
}

.mini-msg-bubble--own .mini-file-msg {
  background: rgba(0, 80, 40, 0.08);
}

.mini-file-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #dc2626;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.mini-file-icon .bi-file-earmark-word-fill {
  background: #2563eb;
}
.mini-file-icon .bi-file-earmark-excel-fill {
  background: #16a34a;
}
.mini-file-icon .bi-file-earmark-ppt-fill {
  background: #ea580c;
}
.mini-file-icon .bi-file-earmark-zip-fill {
  background: #7c3aed;
}

.mini-file-info {
  flex: 1;
  min-width: 0;
}

.mini-file-name {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.mini-file-download {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #25d366;
  text-decoration: none;
}

.mini-file-download:hover {
  text-decoration: underline;
}

/* ===== INPUT AREA ===== */
.mini-chat__input-area {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: white;
  border-top: 1px solid var(--mini-border);
}

.mini-chat__input {
  flex: 1;
  height: 40px;
  padding: 0 14px;
  border: 1px solid var(--mini-border);
  border-radius: 20px;
  font-size: 14px;
  background: #f9fafb;
  outline: none;
  transition: all 0.2s ease;
}

.mini-chat__input:focus {
  border-color: var(--mini-primary);
  background: white;
  box-shadow: 0 0 0 3px rgba(0, 196, 68, 0.1);
}

.mini-chat__send {
  width: 40px;
  height: 40px;
  border: none;
  background: var(--mini-primary);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.mini-chat__send:hover:not(:disabled) {
  background: var(--mini-primary-dark);
  transform: scale(1.05);
}

.mini-chat__send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mini-chat__send-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Blocked state */
.mini-chat__blocked {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: #fef3cd;
  color: #856404;
  font-size: 12px;
}

/* ===== TRANSITIONS ===== */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.bounce-enter-active {
  animation: bounce-in 0.4s ease;
}

.bounce-leave-active {
  animation: bounce-in 0.2s ease reverse;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 576px) {
  .mini-chat__window {
    bottom: 16px;
    right: 16px;
    left: 16px;
    width: auto;
    height: calc(100vh - 100px);
    max-height: 600px;
  }

  .mini-chat__fab {
    bottom: 16px;
    right: 16px;
    width: 54px;
    height: 54px;
    font-size: 22px;
  }

  /* RTL Mobile */
  [dir="rtl"] .mini-chat__fab,
  html[lang="ar"] .mini-chat__fab {
    right: auto;
    left: 16px;
  }

  .mini-chat-img {
    width: 130px;
    height: 130px;
  }
}

/* =============================================
   DARK MODE STYLES - Mini Chat
   ============================================= */
.mini-chat-dark {
  background: #1a1a2e;
  border-color: #2d2d44;
}

.mini-chat-dark .mini-chat__header {
  background: linear-gradient(135deg, #1a1a2e 0%, #252542 100%);
  border-color: #2d2d44;
}

.mini-chat-dark .mini-chat__title {
  color: #e5e5e5;
}

.mini-chat-dark .mini-chat__back {
  background: #2d2d44;
  color: #9ca3af;
}

.mini-chat-dark .mini-chat__back:hover {
  background: #3d3d54;
  color: #e5e5e5;
}

.mini-chat-dark .mini-chat__expand,
.mini-chat-dark .mini-chat__close {
  color: #9ca3af;
}

.mini-chat-dark .mini-chat__expand:hover,
.mini-chat-dark .mini-chat__close:hover {
  background: #2d2d44;
  color: #e5e5e5;
}

/* Conversations List - Dark Mode */
.mini-chat-dark .mini-chat__conv-item:hover {
  background: #2d2d44;
}

.mini-chat-dark .mini-chat__conv-name {
  color: #e5e5e5;
}

.mini-chat-dark .mini-chat__conv-preview {
  color: #9ca3af;
}

/* Messages Area - Dark Mode */
.mini-chat-dark .mini-chat__messages,
.mini-chat-dark .mini-chat__messages-loading {
  background-color: #0d1f15;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cg fill='%234a9c5d' fill-opacity='0.1'%3E%3Cpath d='M10 10L14 6l4 4-4 4z'/%3E%3Ccircle cx='50' cy='15' r='3'/%3E%3Cpath d='M70 8h6v4h-6z'/%3E%3Cpath d='M15 35l3-5h6l3 5-3 5h-6z'/%3E%3Crect x='45' y='32' width='8' height='10' rx='1'/%3E%3Ccircle cx='72' cy='38' r='4'/%3E%3Cpath d='M8 58l6 3-6 3z'/%3E%3Cpath d='M35 55l5 8h-10z'/%3E%3Crect x='60' y='55' width='12' height='6' rx='2'/%3E%3Ccircle cx='20' cy='72' r='3'/%3E%3Cpath d='M48 70l4-4 4 4-4 4z'/%3E%3Cpath d='M68 68h8v8h-8z' fill-opacity='0.07'/%3E%3C/g%3E%3C/svg%3E");
  color: #9ca3af;
}

/* Message Bubbles - Dark Mode */
.mini-chat-dark .mini-msg-bubble {
  background: #1e2a30;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.mini-chat-dark .mini-msg-bubble--own {
  background: linear-gradient(135deg, #0a5c36 0%, #064e2b 100%);
}

.mini-chat-dark .mini-msg-bubble--failed {
  background: linear-gradient(135deg, #4a1c1c 0%, #3d1515 100%);
  border-color: #7f2d2d;
}

.mini-chat-dark .mini-msg-text {
  color: #e5e5e5;
}

.mini-chat-dark .mini-msg-bubble--own .mini-msg-text {
  color: #ffffff;
}

.mini-chat-dark .mini-msg-time {
  color: #9ca3af;
}

.mini-chat-dark .mini-msg-bubble--own .mini-msg-time {
  color: rgba(255, 255, 255, 0.7);
}

/* Voice Message - Dark Mode */
.mini-chat-dark .mini-voice-msg {
  background: linear-gradient(135deg, #1e2a30 0%, #263238 100%);
}

.mini-chat-dark .mini-msg-bubble--own .mini-voice-msg {
  background: linear-gradient(135deg, #064e2b 0%, #0a5c36 100%);
}

/* File Message - Dark Mode */
.mini-chat-dark .mini-file-msg {
  background: #1e2a30;
}

.mini-chat-dark .mini-msg-bubble--own .mini-file-msg {
  background: rgba(255, 255, 255, 0.1);
}

.mini-chat-dark .mini-file-name {
  color: #e5e5e5;
}

.mini-chat-dark .mini-file-download {
  color: #4ade80;
}

/* Image - Dark Mode */
.mini-chat-dark .mini-chat-img {
  background: #1e2a30;
}

/* Input Area - Dark Mode */
.mini-chat-dark .mini-chat__input-area {
  background: #1a1a2e;
  border-color: #2d2d44;
}

.mini-chat-dark .mini-chat__input {
  background: #2d2d44;
  border-color: #3d3d54;
  color: #e5e5e5;
}

.mini-chat-dark .mini-chat__input::placeholder {
  color: #6b7280;
}

.mini-chat-dark .mini-chat__input:focus {
  border-color: #4ade80;
  background: #2d2d44;
  box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.1);
}

.mini-chat-dark .mini-chat__send {
  background: var(--mini-primary);
}

/* Loading & Empty States - Dark Mode */
.mini-chat-dark .mini-chat__loading,
.mini-chat-dark .mini-chat__empty,
.mini-chat-dark .mini-chat__empty-list {
  color: #9ca3af;
}

.mini-chat-dark .mini-chat__empty i,
.mini-chat-dark .mini-chat__empty-list i {
  color: #4b5563;
}

/* Blocked State - Dark Mode */
.mini-chat-dark .mini-chat__blocked {
  background: #3d3520;
  color: #fbbf24;
}

/* Scrollbar - Dark Mode */
.mini-chat-dark .mini-chat__messages::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.mini-chat-dark .mini-chat__conversations::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

/* Conversations List - Dark Mode */
.mini-chat-dark .mini-chat__list {
  background: #1a1a2e;
}

.mini-chat-dark .mini-chat__conversations {
  background: #1a1a2e;
}

.mini-chat-dark .mini-chat__conv-item {
  border-bottom: 1px solid #2d2d44;
}

.mini-chat-dark .mini-chat__conv-item:hover {
  background: #2d2d44;
}

.mini-chat-dark .mini-chat__conv-name {
  color: #e5e5e5;
}

.mini-chat-dark .mini-chat__conv-preview {
  color: #9ca3af;
}

.mini-chat-dark .mini-chat__conv-time {
  color: #6b7280;
}

.mini-chat-dark .mini-chat__conv-badge {
  background: #4ade80;
}

/* Spinner - Dark Mode */
.mini-chat-dark .mini-chat__spinner {
  border-color: #3d3d54;
  border-top-color: #4ade80;
}

/* =============================================
   SEARCH FIELD
   ============================================= */
.mini-chat__search {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.mini-chat__search-icon {
  color: #9ca3af;
  font-size: 13px;
  flex-shrink: 0;
}

.mini-chat__search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #1f2937;
  outline: none;
  min-width: 0;
}

.mini-chat__search-input::placeholder {
  color: #9ca3af;
}

.mini-chat__search-clear {
  width: 20px;
  height: 20px;
  border: none;
  background: #e5e7eb;
  color: #6b7280;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.mini-chat__search-clear:hover {
  background: #d1d5db;
  color: #374151;
}

/* =============================================
   FILTER TABS (reused from Master.vue)
   ============================================= */
.mini-chat__filter-tabs {
  display: flex;
  padding: 6px 10px;
  gap: 4px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.mini-chat__filter-tab {
  flex: 0 1 auto;
  padding: 4px 10px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
  font-weight: 500;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.mini-chat__filter-tab:hover {
  background: #e5e7eb;
  color: #374151;
}

.mini-chat__filter-tab.active {
  background: #00c444;
  color: white;
  font-weight: 600;
}

/* =============================================
   HEADER CONV INFO & LAST SEEN
   ============================================= */
.mini-chat__header-conv-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}

.mini-chat__subtitle {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mini-chat__typing-text {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #a7f3d0;
  font-style: italic;
}

/* Typing dots animation */
.mini-typing-dots {
  display: inline-flex;
  gap: 2px;
  align-items: center;
}

.mini-typing-dots span {
  width: 4px;
  height: 4px;
  background: #a7f3d0;
  border-radius: 50%;
  animation: mini-typing-bounce 1.4s infinite ease-in-out both;
}

.mini-typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}
.mini-typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}
.mini-typing-dots span:nth-child(3) {
  animation-delay: 0s;
}

@keyframes mini-typing-bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* =============================================
   CONVERSATION ACTIONS MENU (Block/Unblock)
   ============================================= */
.mini-chat__conv-menu-wrap {
  position: relative;
}

.mini-chat__menu-btn,
.mini-chat__mia-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white !important;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  text-decoration: none;
}

.mini-chat__menu-btn i,
.mini-chat__mia-btn i {
  color: white !important;
}

.mini-chat__menu-btn:hover,
.mini-chat__mia-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.mini-chat__mia-btn .mia-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.mini-chat__conv-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;
  min-width: 170px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 4px 0;
  z-index: 10;
  transform-origin: top right;
}

.mini-chat__conv-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  background: transparent;
  border: none;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
}

.mini-chat__conv-dropdown-item:hover {
  background: #f0fdf4;
  color: #00c444;
}

.mini-chat__conv-dropdown-item i {
  font-size: 15px;
  width: 18px;
  text-align: center;
}

.mini-chat__conv-dropdown-item--danger {
  color: #dc2626;
}

.mini-chat__conv-dropdown-item--danger:hover {
  background: #fef2f2;
  color: #dc2626;
}

.mini-chat__conv-dropdown-item--success {
  color: #16a34a;
}

.mini-chat__conv-dropdown-item--success:hover {
  background: #f0fdf4;
  color: #16a34a;
}

/* Menu fade transition */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: all 0.2s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.95);
}

/* =============================================
   BLOCKER BANNER (Flutter sync: _buildBlockerBanner)
   ============================================= */
.mini-chat__blocker-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #eff6ff;
  border-top: 1px solid #bfdbfe;
  color: #1e40af;
  font-size: 12px;
  flex-shrink: 0;
}

.mini-chat__blocker-banner i {
  font-size: 14px;
  flex-shrink: 0;
}

.mini-chat__blocker-banner span {
  flex: 1;
}

.mini-chat__blocker-banner-btn {
  padding: 4px 10px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease;
}

.mini-chat__blocker-banner-btn:hover {
  background: #2563eb;
}

/* =============================================
   MIA SUMMARY DIALOG (Flutter sync: _showChatSummary)
   ============================================= */
.mini-chat__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  border-radius: 20px;
  padding: 16px;
}

.mini-chat__summary-dialog {
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 340px;
  max-height: 420px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mini-chat__summary-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
}

.mini-chat__summary-header .mia-header-icon {
  font-size: 18px;
  color: white !important;
}

.mini-chat__summary-header span {
  flex: 1;
}

.mini-chat__summary-close {
  width: 26px;
  height: 26px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white !important;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: background 0.15s;
}

.mini-chat__summary-close i {
  color: white !important;
}

.mini-chat__summary-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.mini-chat__summary-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px;
}

.mini-chat__summary-presets {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}

.mini-chat__summary-chip {
  padding: 5px 12px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mini-chat__summary-chip:hover {
  border-color: #00c444;
  color: #00a038;
}

.mini-chat__summary-chip.active {
  background: #00c444;
  color: white;
  border-color: #00c444;
  font-weight: 600;
}

.mini-chat__summary-dates {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.mini-chat__summary-date-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #374151;
  font-weight: 500;
}

.mini-chat__summary-date-label i {
  color: #8b5cf6;
}

.mini-chat__summary-date-input {
  flex: 1;
  padding: 5px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  color: #374151;
  outline: none;
  transition: border-color 0.15s;
}

.mini-chat__summary-date-input:focus {
  border-color: #8b5cf6;
}

.mini-chat__summary-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px 0;
  color: #6b7280;
  font-size: 13px;
}

.mini-chat__summary-result {
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  border-radius: 10px;
  overflow: hidden;
}

.mini-chat__summary-result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #ede9fe;
  font-size: 12px;
  font-weight: 600;
  color: #6d28d9;
}

.mini-chat__summary-copy {
  width: 26px;
  height: 26px;
  border: none;
  background: rgba(109, 40, 217, 0.1);
  color: #6d28d9;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: background 0.15s;
}

.mini-chat__summary-copy:hover {
  background: rgba(109, 40, 217, 0.2);
}

.mini-chat__summary-text {
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.6;
  color: #374151;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 160px;
  overflow-y: auto;
}

.mini-chat__summary-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  color: #dc2626;
  font-size: 13px;
}

.mini-chat__summary-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid #e5e7eb;
}

.mini-chat__summary-cancel {
  padding: 6px 14px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.mini-chat__summary-cancel:hover {
  background: #f9fafb;
  color: #374151;
}

.mini-chat__summary-submit {
  padding: 6px 14px;
  border: none;
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%);
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.15s;
}

.mini-chat__summary-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #00a038 0%, #008830 100%);
}

.mini-chat__summary-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.mini-chat__summary-submit i {
  color: white !important;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* =============================================
   DARK MODE - New Features
   ============================================= */
/* Search - Dark Mode */
.mini-chat-dark .mini-chat__search {
  background: #1a1a2e;
  border-color: #2d2d44;
}

.mini-chat-dark .mini-chat__search-input {
  color: #e5e5e5;
}

.mini-chat-dark .mini-chat__search-input::placeholder {
  color: #6b7280;
}

.mini-chat-dark .mini-chat__search-icon {
  color: #6b7280;
}

.mini-chat-dark .mini-chat__search-clear {
  background: #2d2d44;
  color: #9ca3af;
}

/* Filter Tabs - Dark Mode */
.mini-chat-dark .mini-chat__filter-tabs {
  background: #1a1a2e;
  border-color: #2d2d44;
}

.mini-chat-dark .mini-chat__filter-tab {
  background: #2d2d44;
  color: #9ca3af;
}

.mini-chat-dark .mini-chat__filter-tab:hover {
  background: #3d3d54;
  color: #e5e5e5;
}

.mini-chat-dark .mini-chat__filter-tab.active {
  background: #00c444;
  color: white;
}

/* Conv Dropdown - Dark Mode */
.mini-chat-dark .mini-chat__conv-dropdown {
  background: #1f2937;
  border-color: #374151;
}

.mini-chat-dark .mini-chat__conv-dropdown-item {
  color: #e5e5e5;
}

.mini-chat-dark .mini-chat__conv-dropdown-item:hover {
  background: #0d3320;
  color: #4ade80;
}

.mini-chat-dark .mini-chat__conv-dropdown-item--danger {
  color: #f87171;
}

.mini-chat-dark .mini-chat__conv-dropdown-item--danger:hover {
  background: #3d1515;
  color: #f87171;
}

/* Blocker Banner - Dark Mode */
.mini-chat-dark .mini-chat__blocker-banner {
  background: #1e293b;
  border-color: #334155;
  color: #93c5fd;
}

.mini-chat-dark .mini-chat__blocker-banner-btn {
  background: #3b82f6;
}

/* Summary Dialog - Dark Mode */
.mini-chat-dark .mini-chat__summary-dialog {
  background: #1f2937;
}

.mini-chat-dark .mini-chat__summary-body {
  color: #e5e5e5;
}

.mini-chat-dark .mini-chat__summary-chip {
  background: #374151;
  border-color: #4b5563;
  color: #9ca3af;
}

.mini-chat-dark .mini-chat__summary-chip:hover {
  border-color: #a78bfa;
  color: #c4b5fd;
}

.mini-chat-dark .mini-chat__summary-chip.active {
  background: #7c3aed;
  border-color: #7c3aed;
  color: white;
}

.mini-chat-dark .mini-chat__summary-date-label {
  color: #d1d5db;
}

.mini-chat-dark .mini-chat__summary-date-input {
  background: #374151;
  border-color: #4b5563;
  color: #e5e5e5;
}

.mini-chat-dark .mini-chat__summary-result {
  background: #2d2044;
  border-color: #4c3370;
}

.mini-chat-dark .mini-chat__summary-result-header {
  background: #3b2960;
  color: #c4b5fd;
}

.mini-chat-dark .mini-chat__summary-text {
  color: #d1d5db;
}

.mini-chat-dark .mini-chat__summary-footer {
  border-color: #374151;
}

.mini-chat-dark .mini-chat__summary-cancel {
  background: #374151;
  border-color: #4b5563;
  color: #d1d5db;
}
</style>
